/**
 * The SEAM onto `@vis.gl/react-google-maps` — the one module in the app that is
 * allowed to name that package's marker primitives, and the reason the app no
 * longer speaks its anchor convention.
 *
 * ── THE CLASS THIS CLOSES ───────────────────────────────────────────────────
 *
 * `AdvancedMarker` forwards `anchorLeft` / `anchorTop` verbatim to
 * `google.maps.marker.AdvancedMarkerElement`, where they are an OFFSET FROM THE
 * CONTENT'S TOP-LEFT CORNER whose defaults are `-50%` / `-100%` — i.e. the sign
 * is inverted relative to every "anchor point" convention a reader brings with
 * them. (The library's own compatibility shim for the deprecated `anchorPoint`
 * prop proves it: `useAdvancedMarkerAnchoring` converts `['50%','100%']` into
 * `anchorLeft = calc(-1 * 50%)`.) The listings map wrote `anchorLeft="50%"`
 * meaning "centre", got "one full card-width to the RIGHT", and shipped — the
 * card's centre measured 320 px off the pin's on production.
 *
 * A convention that reads backwards is not a thing to remember; it is a thing
 * to make unspellable. So the three anchor props are `Omit`ted from the
 * exported prop type here: **a marker's anchor is bottom-centre, always**, and
 * anything that needs another geometry is a POPOVER — a screen-space rectangle
 * constrained by a screen-space boundary — which is a different mechanism
 * (`map-popover.ts`, Floating UI) and not an anchor at all.
 *
 * The compile-time half is this `Omit`; the import half is the
 * `no-restricted-imports` row in `apps/public/eslint.config.mjs`, which refuses
 * `@vis.gl/react-google-maps` outside `src/shared/components/map/**`. Neither
 * alone is enough: the `Omit` cannot stop a call site importing the raw
 * component, and the lint row cannot stop THIS file from re-exporting the props.
 */
import {
  APILoadingStatus,
  useApiLoadingStatus,
  useMap,
  AdvancedMarker as VisAdvancedMarker,
  type AdvancedMarkerProps as VisAdvancedMarkerProps,
} from '@vis.gl/react-google-maps';
import { useEffect, useState } from 'react';

/**
 * The map pane and the SDK loader, re-exported UNCHANGED — neither has an
 * anchor convention to hide. They pass through the seam anyway so the
 * `no-restricted-imports` row can be TOTAL: a rule with an exception for "the
 * one harmless symbol" is a rule every future import argues with.
 *
 * ⚠ BILLABLE ON MOUNT. Rendering `APIProvider` injects the Maps JS script and
 * rendering `Map` is one Dynamic Maps load — so these two are re-exported for
 * exactly ONE importer, `MapBoundary.tsx`, which mounts them on VIEW (or on a
 * tap) instead of on mount. The `[map-boundary]` rows in
 * `apps/public/eslint.config.mjs` refuse them to every other file, this seam's
 * own directory included. Markers stay importable: a marker is only ever a
 * child of the boundary's `Map`.
 */
export { APIProvider, Map } from '@vis.gl/react-google-maps';
export type { MapCameraChangedEvent } from '@vis.gl/react-google-maps';

/**
 * The props this seam refuses to forward — the third-party anchor convention,
 * named once so the `Omit` below and the doc above cannot drift.
 *
 * `anchorPoint` is the DEPRECATED spelling of the same idea (with the opposite
 * sign again), so it goes too: leaving it would restore the class through the
 * door the library itself marked as legacy.
 */
type MapAnchorProp = 'anchorLeft' | 'anchorTop' | 'anchorPoint';

/** `AdvancedMarker`'s props, minus the anchor convention. */
export type AdvancedMarkerProps = Omit<VisAdvancedMarkerProps, MapAnchorProp>;

/**
 * A map-projected marker whose content hangs bottom-centre from its
 * coordinate — the library's own default, and now the only geometry a call
 * site can ask for.
 */
export function AdvancedMarker(props: AdvancedMarkerProps) {
  return <VisAdvancedMarker {...props} />;
}

// ----------------------------------------------------------------------------
// The two "loaded but unusable" signals MapBoundary renders around (#9393)
// ----------------------------------------------------------------------------
//
// Narrow, boolean-valued readings rather than re-exports, for the reason the
// `Omit` above exists: the `[map-boundary]` package row refuses
// `useApiLoadingStatus` outside this seam, and a re-export would hand the raw
// status enum to every file. What a caller gets here is a fact, not a handle.

/**
 * Google REFUSED this page's key. Must render inside `APIProvider`.
 *
 * vis.gl's own status, and deliberately not `window.gm_authFailure`: vis.gl
 * 1.7.1's loader ASSIGNS that global when it appends the SDK script
 * (`window.gm_authFailure = () => { loadingStatus = AUTH_FAILURE … }`), so any
 * handler the app installed earlier is overwritten before Google can call it.
 * The status is where the callback actually lands.
 */
export function useMapsKeyRefused(): boolean {
  return useApiLoadingStatus() === APILoadingStatus.AUTH_FAILURE;
}

/**
 * The slice of `google.maps.Map` the capability reading uses — typed here
 * because the `google.maps.*` ambient types are not visible to this app's
 * type-checker (see `MapBoundaryMapOptions`), so `useMap()` is read as
 * `unknown` and NARROWED by {@link isMapCapabilitySource}, never cast.
 */
interface MapCapabilitySource {
  getMapCapabilities: () => { isAdvancedMarkersAvailable?: boolean };
  addListener: (event: 'mapcapabilities_changed', handler: () => void) => { remove: () => void };
}

function isMapCapabilitySource(value: unknown): value is MapCapabilitySource {
  return (
    typeof value === 'object' &&
    value !== null &&
    'getMapCapabilities' in value &&
    typeof value.getMapCapabilities === 'function' &&
    'addListener' in value &&
    typeof value.addListener === 'function'
  );
}

/**
 * The map reported Advanced Markers UNAVAILABLE. Must render inside `Map`.
 *
 * `true` from the FIRST `isAdvancedMarkersAvailable === false` the SDK reports,
 * and LATCHED there (a later `true` does not un-report it). `undefined` and `true` are
 * never read as a failure — measured against v3.66, a map's capabilities start
 * `true` at construction and change on `mapcapabilities_changed`; on a map with
 * no usable Map ID the SDK reported `false` there and, ~50 ms later, a marker
 * opened the SDK's modal "Error" dialog — which this reading lets the boundary
 * unmount (#9393).
 */
export function useAdvancedMarkersUnavailable(): boolean {
  const instance: unknown = useMap();
  const map = isMapCapabilitySource(instance) ? instance : null;
  const [unavailable, setUnavailable] = useState(false);
  useEffect(() => {
    if (!map) return;
    const read = () => {
      if (map.getMapCapabilities().isAdvancedMarkersAvailable === false) setUnavailable(true);
    };
    read();
    const listener = map.addListener('mapcapabilities_changed', read);
    return () => listener.remove();
  }, [map]);
  return unavailable;
}
